import { get, post } from '../requestNew';
import { CommandRequest, CommandResult, MessageDetail, MessagePage, MessageQuery, MessageStats, ReviewRequest } from './types';

const ROOT = '/agent/admin/v1/messages';
interface Envelope<T> {
  code: number;
  message?: string;
  data: T;
}
export class MessageApiError extends Error {
  constructor(
    message: string,
    public status?: number
  ) {
    super(message);
  }
}
export function normalizeError(error: unknown): MessageApiError {
  const value = error as {
    response?: { status?: number; data?: { message?: string } };
    code?: unknown;
    status?: number;
    message?: string;
  };
  return new MessageApiError(
    value?.response?.data?.message || value?.message || '请求失败',
    value?.response?.status || value?.status || (typeof value?.code === 'number' ? value.code : undefined)
  );
}
async function unwrap<T>(promise: Promise<Envelope<T>>): Promise<T> {
  try {
    const result = await promise;
    if (result?.code !== 200 || result.data == null)
      throw new MessageApiError(result?.message || '快讯接口数据异常', result?.code);
    return result.data;
  } catch (error) {
    throw normalizeError(error);
  }
}
// Keep existing base URL, JWT storage and login/error behavior in requestNew.
const inlineError = () => undefined;
export const messageApi = {
  list: (query: MessageQuery) => {
    const params: Record<string, string | number> = { page: query.page, size: query.size, q: query.q };
    if (query.aiPhase) params.aiPhase = query.aiPhase;
    if (query.reviewState) params.reviewState = query.reviewState;
    if (query.publicationState) params.publicationState = query.publicationState;
    return unwrap(get<Envelope<MessagePage>>(ROOT, params, inlineError));
  },
  stats: () => unwrap(get<Envelope<MessageStats>>(`${ROOT}/stats`, {}, inlineError)),
  detail: (id: string) => unwrap(get<Envelope<MessageDetail>>(`${ROOT}/${encodeURIComponent(id)}`, {}, inlineError)),
  save: (id: string, body: ReviewRequest) =>
    unwrap(
      post<Envelope<{ reviewId: string; editorialVersion: string }>>(
        `${ROOT}/${encodeURIComponent(id)}/reviews`,
        body,
        inlineError
      )
    ),
  command: (id: string, body: CommandRequest) =>
    unwrap(post<Envelope<CommandResult>>(`${ROOT}/${encodeURIComponent(id)}/commands`, body, inlineError)),
  commandStatus: (uuid: string) =>
    unwrap(get<Envelope<CommandResult>>(`${ROOT}/commands/${encodeURIComponent(uuid)}`, {}, inlineError))
};

export function commandUuid(): string {
  const crypto = window.crypto;
  if (typeof crypto?.randomUUID === 'function') return crypto.randomUUID();
  if (!crypto?.getRandomValues) throw new MessageApiError('当前浏览器不支持安全命令编号，请更换浏览器');
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
